// ============================================================================
// SourceField.tsx — УНИВЕРСАЛЬНОЕ «поле с источником» (как в 1С подсказки/
// подбор): автодополнение из внешних API (ExternalAPI, провайдер-агностик),
// окно выбора похожих вариантов («...»), авто-заполнение формы по маппингу.
//
// Настройка поля (metadata_fields.source_settings, SSoT — сервер):
//   { "api": "geo", "operation": "find-party" | "suggest-address",
//     "queryField": "inn", "mapping": [{ "from": "name", "to": "name" }, ...] }
//   api: "geo" — ЕДИНЫЙ ГЕО-ШОВ (GeoService). Совместимость: "dadata" — АЛИАС
//   того же шва (существующие формы не ломаются) — обе строки идут в sdk.geo.
//
// Один компонент на все случаи: контрагенты (find-party), адреса
// (suggest-address → GeoService.Resolve), физлица и т.д.
// ============================================================================

import { useCallback, useEffect, useRef, useState } from 'react';
import { Search, X } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { UniversalWindow } from '@/components/ui/UniversalWindow';
import { getSDK } from '@/store/sdkStore';
import { friendlyError } from '@/lib/errors';

export interface SourceMapping {
  from: string; // поле ответа источника (нормализованное)
  to: string;   // поле формы/объекта
}

export interface SourceSettings {
  api: string;            // 'geo' (алиас 'dadata')
  operation: string;      // 'find-party' | 'suggest-address'
  queryField?: string;    // поле формы, значение которого — запрос (например 'inn')
  mapping?: SourceMapping[];
}

export interface SourceSuggestion {
  value: string;           // отображаемое
  label: string;           // подпись (вторая строка)
  raw: Record<string, unknown>; // нормализованный ответ источника (для маппинга)
}

interface SourceFieldProps {
  source: SourceSettings;
  /** Значение запроса (если queryField не задан — текст поля) */
  query?: string;
  disabled?: boolean;
  placeholder?: string;
  /** Выбрана подсказка: маппинг уже применён? НЕТ — передаём сырые данные,
   *  родитель применяет mapping сам (знает все поля формы) */
  onPicked: (suggestion: SourceSuggestion, mapping: SourceMapping[]) => void;
}

// Запрос к источнику (ЕДИНЫЙ ГЕО-ШОВ GeoService): операция → нормализованные
// подсказки. api 'dadata' — алиас гео-шва (dual-read, старые формы работают).
async function fetchSuggestions(source: SourceSettings, q: string): Promise<SourceSuggestion[]> {
  const sdk = getSDK();
  const query = q.trim();
  if (!query) return [];
  if (source.operation === 'find-party') {
    const r: any = await sdk.geo.findParty({ inn: query });
    const p: any = r?.party;
    if (!p?.found) return [];
    return [{
      value: p.name || query,
      label: `${p.type === 'individual' ? 'ИП' : 'Юр. лицо'} · ${p.inn}${p.status && p.status !== 'active' ? ' · ' + p.status : ''}`,
      raw: p,
    }];
  }
  if (source.operation === 'suggest-address') {
    const r: any = await sdk.geo.suggestAddress({ query, count: 10 });
    return ((r?.items || []) as any[]).map((it) => ({
      value: it.value || '',
      label: it.unrestrictedValue || it.value || '',
      raw: it,
    }));
  }
  return [];
}

export function SourceField({ source, query, disabled, placeholder, onPicked }: SourceFieldProps) {
  const [text, setText] = useState(query || '');
  const [suggestions, setSuggestions] = useState<SourceSuggestion[]>([]);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [pickerOpen, setPickerOpen] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (query !== undefined) setText(query);
  }, [query]);

  const run = useCallback(async (q: string) => {
    if (!q.trim()) { setSuggestions([]); setOpen(false); return; }
    setBusy(true);
    setError('');
    try {
      const items = await fetchSuggestions(source, q);
      setSuggestions(items);
      setOpen(items.length > 0);
    } catch (e: any) {
      setError(friendlyError(e));
      setOpen(false);
    } finally {
      setBusy(false);
    }
  }, [source]);

  const onChange = (v: string) => {
    setText(v);
    setOpen(false);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => void run(v), 300);
  };

  const pick = (s: SourceSuggestion) => {
    setOpen(false);
    setText(s.value);
    onPicked(s, source.mapping || []);
  };

  // Запрос: значение поля-источника (queryField) или текст поля
  const effectiveQuery = source.queryField ? (query || '') : text;

  return (
    <div className="relative">
      <div className="flex items-center gap-1">
        <div className="relative flex-1 min-w-0">
          <Input
            value={text}
            onChange={(e) => onChange(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && suggestions.length > 0) { e.preventDefault(); pick(suggestions[0]); }
              if (e.key === 'Escape') setOpen(false);
            }}
            onFocus={() => { if (suggestions.length > 0) setOpen(true); }}
            onBlur={() => setTimeout(() => setOpen(false), 200)}
            disabled={disabled}
            placeholder={placeholder || 'Введите запрос для подбора…'}
            className="h-8 text-sm"
          />
          {busy && <Search className="absolute right-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground animate-pulse" />}
        </div>
        {/* Окно выбора похожих вариантов (если их несколько) */}
        <Button
          size="sm"
          variant="outline"
          className="h-8 px-2 text-xs shrink-0"
          title="Выбрать из найденных"
          disabled={disabled || !effectiveQuery.trim()}
          onClick={() => {
            if (suggestions.length === 0) void run(effectiveQuery);
            setPickerOpen(true);
          }}
        >
          ...
        </Button>
      </div>

      {/* Выпадающий список автодополнения */}
      {open && suggestions.length > 0 && (
        <div className="absolute z-50 mt-1 w-full border rounded-md bg-popover shadow-lg max-h-56 overflow-auto">
          {suggestions.map((s, i) => (
            <button
              key={i}
              className="w-full text-left px-3 py-2 text-xs hover:bg-accent flex flex-col gap-0.5"
              onMouseDown={(e) => { e.preventDefault(); pick(s); }}
            >
              <span className="font-medium">{s.value}</span>
              {s.label !== s.value && <span className="text-[10px] text-muted-foreground truncate">{s.label}</span>}
            </button>
          ))}
        </div>
      )}

      {error && <div className="text-[10px] text-destructive mt-0.5">{error}</div>}

      {/* Окно выбора (множество похожих вариантов) */}
      <UniversalWindow open={pickerOpen} onClose={() => setPickerOpen(false)} title="Выбор значения" blocking width="max-w-xl">
        <div className="p-3 space-y-2">
          <Input
            value={effectiveQuery}
            onChange={(e) => { setText(e.target.value); void run(e.target.value); }}
            placeholder="Поиск…"
            className="h-8 text-sm"
            autoFocus
          />
          <div className="max-h-[50vh] overflow-auto space-y-1">
            {suggestions.length === 0 && <div className="text-xs text-muted-foreground italic p-2">Ничего не найдено. Уточните запрос.</div>}
            {suggestions.map((s, i) => (
              <button
                key={i}
                className="w-full text-left px-3 py-2 border rounded-md hover:bg-accent flex flex-col gap-0.5"
                onClick={() => { pick(s); setPickerOpen(false); }}
              >
                <span className="text-xs font-medium">{s.value}</span>
                {s.label !== s.value && <span className="text-[10px] text-muted-foreground">{s.label}</span>}
              </button>
            ))}
          </div>
          <div className="flex justify-end">
            <Button size="sm" variant="outline" className="h-7 text-[11px]" onClick={() => setPickerOpen(false)}>
              <X className="h-3 w-3 mr-1" /> Закрыть
            </Button>
          </div>
        </div>
      </UniversalWindow>
    </div>
  );
}
